import { ZfDialog, ZfDialogContent, ZfDialogFooter, ZfDialogHeader, ZfIcon, ZfIconButton, ZfPill, ZfStack, ZfTextButton } from '@zafire/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import moment from 'moment/moment';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useDeleteAdvisoryNeedTask, useDeleteLifeEvent } from '../../api';
import type { AdvisoryNeedTaskDto, LifeEventDto, MaturityDto, QualifiedIdentity } from '../../api/generated/client';
import { DEFAULT_INPUT_DATE_FORMAT } from '../../config/constants';
import { useRoadmapPageContext } from '../../contexts/roadmap-page-context';
import { useActiveEvent } from '../../hooks/useActiveEvent';
import { useBalanceEntries } from '../../hooks/useBalanceAccounts';
import type { RoadmapEvent, RoadmapTableData } from '../../interfaces/event';
import { getRoadmapEntryDisplayName, isAdvisoryNeedTask, isLifeEvent, isRefinanced } from '../../utils/eventUtils';
import { formatAmountCurrencyFirst } from '../../utils/renderUtils';
import { getComparator } from '../../utils/utils';
import { AgGridTable, OverflowTooltip } from '../atoms';
import { useEventApiRequest } from '../modal/eventModal/useEventApiRequest';
import { NeedEventModal } from '../topic/modals/NeedEventModal';
import { BalanceEntryModalWrapper } from '../wealth';
import { tableGroupingSorter } from './tableGroupingSorter';

// roadmap title (40 px) + page content padding top/bottom (80 px) + table margin-top (32 px)
const HEADER_TITLE_AND_PADDINGS_HEIGHT = 152;

interface IProps {
    allEvents: RoadmapEvent[];
    onEventReminderClick: (item: RoadmapEvent) => void;
}

export const RoadmapTable: FunctionComponent<IProps> = ({ allEvents, onEventReminderClick }) => {
    const { t } = useTranslation();
    const { showCompletedEvents } = useRoadmapPageContext();
    const { data: client } = useClient();
    const balanceEntries = useBalanceEntries();
    const deleteLifeEvent = useDeleteLifeEvent();
    const deleteAdvisoryNeedTask = useDeleteAdvisoryNeedTask();
    const { updateEvent, inProgress } = useEventApiRequest();

    const [activeEvent, setActiveEvent] = useActiveEvent();
    const [eventToEdit, setEventToEdit] = useState<RoadmapTableData | undefined>(undefined);
    const [selectedEvent, setSelectedEvent] = useState<RoadmapTableData | undefined>(undefined);

    useEffect(() => {
        if (activeEvent) {
            setEventToEdit(activeEvent as RoadmapTableData);
        }
    }, [activeEvent]);

    const onCloseEditingModal = () => {
        setEventToEdit(undefined);
        setActiveEvent(undefined);
    };

    const handleEditClick = (roadmapEvent: RoadmapTableData) => setEventToEdit(roadmapEvent);

    const handleDeleteClick = () => {
        if (selectedEvent?.eventType === 'MATURITY') {
            return;
        }
        if (isLifeEvent(selectedEvent)) {
            deleteLifeEvent.mutate(selectedEvent.id!);
        }
        if (isAdvisoryNeedTask(selectedEvent)) {
            deleteAdvisoryNeedTask.mutate(selectedEvent.id);
        }
        setSelectedEvent(undefined);
    };

    const getBalanceEntryById = (qualifiedIdentity: QualifiedIdentity) => balanceEntries.find((entry) => entry.qualifiedKey === qualifiedIdentity.qualifiedKey);
    const showEventModal = (event?: RoadmapEvent): event is RoadmapEvent<AdvisoryNeedTaskDto | LifeEventDto> => event != null && event.eventType !== 'MATURITY';
    const showClmWebComponent = (event?: RoadmapEvent): event is RoadmapEvent<MaturityDto> => event != null && event.eventType === 'MATURITY';

    const eventsData: (RoadmapEvent & { strategy: string })[] = useMemo(
        () =>
            allEvents
                .sort(getComparator('asc', 'dueDate'))
                .filter((roadmapEvent) => showCompletedEvents || ['DEFINED', 'IN_PROGRESS'].includes(roadmapEvent.status!))
                .map((event) => ({
                    ...event,
                    strategy: t(moment(event.dueDate ?? '').diff(new Date(), 'days') < 0 ? 'pastEvents' : 'upcomingEvents'),
                })),
        [allEvents, showCompletedEvents, t],
    );

    const cellRenderer = useCallback(
        ({ data, value: cellValue }: ICellRendererParams, customValue?: string): ReactNode => {
            const value = String(customValue ?? cellValue ?? '');

            if (data?.strategy === t('pastEvents')) return <span style={{ color: 'var(--fg-color-default-normal)' }}>{value}</span>;

            return value;
        },
        [t],
    );

    const columnDefs: ColDef<RoadmapTableData>[] = useMemo(
        () => [
            { field: 'strategy', rowGroup: true, hide: true },
            {
                field: 'name',
                headerName: t('roadmapColHeaderTitle'),
                minWidth: 300,
                pinned: 'left',
                resizable: true,
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) =>
                    cellRenderer(params, getRoadmapEntryDisplayName(params.data, t('refinancedLabel'))),
                comparator: tableGroupingSorter,
            },
            {
                field: 'eventType',
                headerName: t('roadmapColHeaderType'),
                minWidth: 120,
                pinned: 'left',
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) => cellRenderer(params, t(params.value)),
                comparator: tableGroupingSorter,
            },
            {
                field: 'note',
                headerName: t('roadmapColHeaderCommentNextSteps'),
                minWidth: 300,
                cellRenderer: ({ data, value }: ICellRendererParams<RoadmapTableData>) => (
                    <OverflowTooltip
                        text={value}
                        width={300}
                        padding={24}
                        styles={{ ...(data?.strategy === t('pastEvents') ? { color: 'var(--fg-color-default-normal)' } : {}) }}
                    />
                ),
                comparator: tableGroupingSorter,
            },
            {
                field: 'status',
                headerName: t('roadmapColHeaderStatus'),
                width: 175,
                editable: true,
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) =>
                    cellRenderer(params, `${t(`eventStatus-${params.value}`)}${inProgress ? '<zf-loading-spinner size="small" />' : ''}`),
                cellClass: 'editable-cell',
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                    values: ['DEFINED', 'IN_PROGRESS', 'COMPLETED_SUCCESSFULLY', 'COMPLETED_STOPPED'],
                },
                valueFormatter: (params) => (params.value ? t(`eventStatus-${params.value}`) : ''),
                onCellValueChanged: ({ data, newValue }) => {
                    if (newValue) {
                        updateEvent({ ...data, status: newValue });
                    }
                },
                comparator: tableGroupingSorter,
            },
            {
                field: 'notification',
                headerName: t('roadmapColHeaderNotification'),
                width: 160,
                cellRenderer: ({ value }: ICellRendererParams<RoadmapTableData>) =>
                    value && value !== 'NONE' ? (
                        <ZfPill clickable={false} color={value === 'DUE' ? 'blue' : 'yellow'}>
                            <ZfIcon slot="leading" size="small" name={value === 'DUE' ? 'lightbulb' : 'warning'} />
                            {t(`eventNotification-${value}`)}
                        </ZfPill>
                    ) : null,
                comparator: tableGroupingSorter,
            },
            {
                field: 'signedReferenceAmount',
                headerName: t('roadmapColHeaderAmount'),
                minWidth: 135,
                sortable: false,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', justifyContent: 'end', textAlign: 'right' },
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) =>
                    cellRenderer(params, formatAmountCurrencyFirst(params.value, !isRefinanced(params.data), params.data?.referenceCurrency, false)),
            },
            {
                field: 'reminderDate',
                headerName: t('roadmapColHeaderReminderDate'),
                minWidth: 135,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', justifyContent: 'end', textAlign: 'right' },
                comparator: tableGroupingSorter,
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) => cellRenderer(params, moment(params.value).format(DEFAULT_INPUT_DATE_FORMAT)),
            },
            {
                field: 'dueDate',
                headerName: t('taskDueDateLabel'),
                minWidth: 120,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', justifyContent: 'end', textAlign: 'right' },
                comparator: tableGroupingSorter,
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) => cellRenderer(params, moment(params.value).format(DEFAULT_INPUT_DATE_FORMAT)),
            },
            {
                field: 'updatedAt',
                headerName: t('roadmapColHeaderLastUpdatedDate'),
                minWidth: 120,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', justifyContent: 'end', textAlign: 'right' },
                comparator: tableGroupingSorter,
                cellRenderer: (params: ICellRendererParams<RoadmapTableData>) => cellRenderer(params, moment(params.value).format(DEFAULT_INPUT_DATE_FORMAT)),
            },
            {
                headerName: t('roadmapColHeaderActions'),
                minWidth: 130,
                pinned: 'right',
                sortable: false,
                headerClass: 'roadmap-table-actions-header',
                cellStyle: { display: 'flex', justifyContent: 'center', textAlign: 'center' },
                cellRenderer: ({ data }: ICellRendererParams<RoadmapTableData>) =>
                    data ? (
                        <ZfStack direction="row" spacing="extra-extra-small">
                            <ZfIconButton size="extra-small" hierarchy="tertiary" onClick={() => onEventReminderClick(data)}>
                                <ZfIcon name="calendar_today" size="medium" aria-hidden="true" />
                            </ZfIconButton>
                            <ZfIconButton size="extra-small" hierarchy="tertiary" onClick={() => handleEditClick(data)}>
                                <ZfIcon name="edit" size="medium" aria-hidden="true" />
                            </ZfIconButton>
                            <ZfIconButton
                                disabled={data.eventType === 'MATURITY'}
                                size="extra-small"
                                hierarchy="tertiary"
                                type="destructive"
                                onClick={() => setSelectedEvent(data)}
                            >
                                <ZfIcon name="delete" size="medium" aria-hidden="true" />
                            </ZfIconButton>
                        </ZfStack>
                    ) : null,
            },
        ],
        [t, cellRenderer, inProgress, updateEvent, onEventReminderClick],
    );

    return (
        <>
            {showEventModal(eventToEdit) && <NeedEventModal event={eventToEdit} onClose={onCloseEditingModal} isOpen={true} />}
            <AgGridTable
                rowData={eventsData}
                columnDefs={columnDefs}
                defaultColDef={{
                    resizable: false,
                    cellStyle: { display: 'flex', alignItems: 'center' },
                }}
                groupDisplayType="groupRows"
                groupDefaultExpanded={-1}
                addTableContainer={true}
                screenHeaderPaddingsAndHeight={HEADER_TITLE_AND_PADDINGS_HEIGHT}
            />
            <ZfDialog size="s" show={!!selectedEvent} showCloseButton={true} onDialogClose={() => setSelectedEvent(undefined)}>
                <ZfDialogHeader>{t('modalConfirmDeleteTitle')}</ZfDialogHeader>
                <ZfDialogContent>{t('modalConfirmDeleteText')}</ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="primary" direction="row" spacing="extra-small">
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => setSelectedEvent(undefined)}>
                            {t('cancel')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" hierarchy="primary" kind="destructive" onClick={handleDeleteClick}>
                            {t('modalDeleteButton')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
            {showClmWebComponent(eventToEdit) && (
                <BalanceEntryModalWrapper
                    woCategoryId={(eventToEdit as MaturityDto).categoryId}
                    partnerUid={client?.partnerUuid}
                    balanceEntry={getBalanceEntryById(eventToEdit as MaturityDto)}
                    onClose={onCloseEditingModal}
                />
            )}
        </>
    );
};
